import type * as schemas from "@/schemas/sections";

// Single source of truth for section schema, role, and render association.
// The schema import above is TYPE-ONLY and erased at compile time, so this file
// has zero runtime cost and is safe for the client bundle even though it
// references every section schema name. Static component names are closed
// below and their real imports are checked by `section-components.ts`.
// `registry/sections.ts` (server/build-only) derives
// SECTION_SCHEMAS from entries carrying a `schema`; `registry/section-
// components.ts` (client, schema-free) derives `sectionRegistry` from
// entries carrying a `component`; the lazy loader and role API derive from
// `lazy` and `role` on the same entry.
export type SchemaKey = Extract<keyof typeof schemas, `${string}SectionSchema`>;
export type ComponentKey = "FooterSection" | "LeadMagnetNextSteps" | "ThankYouRedirect";

export const SECTION_ROLES = [
  "nav",
  "hero",
  "pain-diagnosis",
  "authority",
  "value-prop",
  "qualifier",
  "process",
  "social-proof",
  "pricing",
  "objection-handling",
  "cross-sell",
  "lead-capture",
  "closer",
  "content",
  "footer",
  "utility",
] as const;

export type SectionRole = (typeof SECTION_ROLES)[number];

export interface LazySectionDef {
  exportName: string;
  moduleId: `src/components/kit/${string}/${string}.tsx`;
}

export interface SectionDef {
  schema?: SchemaKey;
  component?: ComponentKey;
  lazy?: LazySectionDef;
  role: SectionRole;
}

function lazyKit<const S extends SchemaKey, const E extends string, const R extends SectionRole>(
  schema: S,
  exportName: E,
  role: R,
) {
  return {
    schema,
    role,
    lazy: {
      exportName,
      moduleId: `src/components/kit/${exportName}/${exportName}.tsx` as const,
    },
  };
}

const manifest = {
  footer: { schema: "FooterSectionSchema", component: "FooterSection", role: "footer" },
  "lead-magnet-next-steps": {
    schema: "LeadMagnetNextStepsSectionSchema",
    component: "LeadMagnetNextSteps",
    role: "closer",
  },
  "kit-takeaways": lazyKit("KitTakeawaysSectionSchema", "KitTakeaways", "value-prop"),
  "kit-trainer": lazyKit("KitTrainerSectionSchema", "KitTrainer", "qualifier"),
  "kit-about-tags": lazyKit("KitAboutTagsSectionSchema", "KitAboutTags", "authority"),
  "kit-career-crisis-quiz": {
    schema: "KitCareerCrisisQuizSectionSchema",
    role: "qualifier",
    lazy: {
      exportName: "KitCareerCrisisQuiz",
      moduleId: "src/components/kit/KitCareerCrisis/KitCareerCrisis.tsx",
    },
  },
  "kit-career-crisis-results": {
    schema: "KitCareerCrisisResultsSectionSchema",
    role: "content",
    lazy: {
      exportName: "KitCareerCrisisResults",
      moduleId: "src/components/kit/KitCareerCrisis/KitCareerCrisis.tsx",
    },
  },
  "kit-career-crisis-plan": {
    schema: "KitCareerCrisisPlanSectionSchema",
    role: "content",
    lazy: {
      exportName: "KitCareerCrisisPlan",
      moduleId: "src/components/kit/KitCareerCrisis/KitCareerCrisis.tsx",
    },
  },
  "kit-career-crisis-hero": {
    schema: "KitCareerCrisisHeroSectionSchema",
    role: "hero",
    lazy: {
      exportName: "KitCareerCrisisHero",
      moduleId: "src/components/kit/KitCareerCrisis/CareerCrisisHero.tsx",
    },
  },
  "kit-career-crisis-author": {
    schema: "KitCareerCrisisAuthorSectionSchema",
    role: "authority",
    lazy: {
      exportName: "KitCareerCrisisAuthor",
      moduleId: "src/components/kit/KitCareerCrisis/CareerCrisisAuthor.tsx",
    },
  },
  "kit-career-crisis-intro": {
    schema: "KitCareerCrisisIntroSectionSchema",
    role: "pain-diagnosis",
    lazy: {
      exportName: "KitCareerCrisisIntro",
      moduleId: "src/components/kit/KitCareerCrisis/CareerCrisisIntro.tsx",
    },
  },
  "kit-career-crisis-missed": {
    schema: "KitCareerCrisisMissedSectionSchema",
    role: "pain-diagnosis",
    lazy: {
      exportName: "KitCareerCrisisMissed",
      moduleId: "src/components/kit/KitCareerCrisis/CareerCrisisMissed.tsx",
    },
  },
  "kit-career-crisis-case": {
    schema: "KitCareerCrisisCaseSectionSchema",
    role: "social-proof",
    lazy: {
      exportName: "KitCareerCrisisCase",
      moduleId: "src/components/kit/KitCareerCrisis/CareerCrisisCase.tsx",
    },
  },
  "kit-career-crisis-offer": {
    schema: "KitCareerCrisisOfferSectionSchema",
    role: "closer",
    lazy: {
      exportName: "KitCareerCrisisOffer",
      moduleId: "src/components/kit/KitCareerCrisis/CareerCrisisOffer.tsx",
    },
  },
  "kit-career-crisis-footer": {
    schema: "KitCareerCrisisFooterSectionSchema",
    role: "footer",
    lazy: {
      exportName: "KitCareerCrisisFooter",
      moduleId: "src/components/kit/KitCareerCrisis/CareerCrisisFooter.tsx",
    },
  },
  "kit-quiz": lazyKit("KitQuizSectionSchema", "KitQuiz", "qualifier"),
  "kit-survey": lazyKit("KitSurveySectionSchema", "KitSurvey", "qualifier"),
  "kit-navbar": lazyKit("KitNavbarSectionSchema", "KitNavbar", "nav"),
  "kit-footer": lazyKit("KitFooterSectionSchema", "KitFooter", "footer"),
  "kit-guide-hero": lazyKit("KitGuideHeroSectionSchema", "KitGuideHero", "hero"),
  "kit-inside-showcase": lazyKit(
    "KitInsideShowcaseSectionSchema",
    "KitInsideShowcase",
    "value-prop",
  ),
  "kit-guide-services": lazyKit("KitGuideServicesSectionSchema", "KitGuideServices", "cross-sell"),
  "kit-faq": lazyKit("KitFaqSectionSchema", "KitFaq", "objection-handling"),
  "kit-guide-final-cta": lazyKit("KitGuideFinalCtaSectionSchema", "KitGuideFinalCta", "closer"),
  "kit-home-hero": lazyKit("KitHomeHeroSectionSchema", "KitHomeHero", "hero"),
  "kit-home-pain": lazyKit("KitHomePainSectionSchema", "KitHomePain", "pain-diagnosis"),
  "kit-home-services": lazyKit("KitHomeServicesSectionSchema", "KitHomeServices", "value-prop"),
  "kit-reviews": lazyKit("KitReviewsSectionSchema", "KitReviews", "social-proof"),
  "kit-market-stat": lazyKit("KitMarketStatSectionSchema", "KitMarketStat", "pain-diagnosis"),
  "kit-error-teaser": lazyKit("KitErrorTeaserSectionSchema", "KitErrorTeaser", "pain-diagnosis"),
  "kit-offer-formats": lazyKit("KitOfferFormatsSectionSchema", "KitOfferFormats", "pricing"),
  "kit-cross-sell": lazyKit("KitCrossSellSectionSchema", "KitCrossSell", "cross-sell"),
  "kit-club-offer": lazyKit("KitClubOfferSectionSchema", "KitClubOffer", "cross-sell"),
  "kit-hero-portrait": lazyKit("KitHeroPortraitSectionSchema", "KitHeroPortrait", "hero"),
  "kit-before-after": lazyKit("KitBeforeAfterSectionSchema", "KitBeforeAfter", "process"),
  "kit-service-tiers": lazyKit("KitServiceTiersSectionSchema", "KitServiceTiers", "pricing"),
  "kit-checklist-island": lazyKit(
    "KitChecklistIslandSectionSchema",
    "KitChecklistIsland",
    "lead-capture",
  ),
  "kit-final-panel": lazyKit("KitFinalPanelSectionSchema", "KitFinalPanel", "closer"),
  "kit-gift-island": lazyKit("KitGiftIslandSectionSchema", "KitGiftIsland", "lead-capture"),
  "kit-gated-directory": lazyKit(
    "KitGatedDirectorySectionSchema",
    "KitGatedDirectory",
    "lead-capture",
  ),
  "kit-guide-gate": lazyKit("KitGuideGateSectionSchema", "KitGuideGate", "lead-capture"),
  "kit-guide-checklist": lazyKit(
    "KitGuideChecklistSectionSchema",
    "KitGuideChecklist",
    "pain-diagnosis",
  ),
  "kit-guide-hacks": lazyKit("KitGuideHacksSectionSchema", "KitGuideHacks", "content"),
  "kit-guide-quiz": lazyKit("KitGuideQuizSectionSchema", "KitGuideQuiz", "content"),
  "kit-guide-builder": lazyKit("KitGuideBuilderSectionSchema", "KitGuideBuilder", "content"),
  "kit-guide-switch": lazyKit("KitGuideSwitchSectionSchema", "KitGuideSwitch", "content"),
  "kit-value-recap": lazyKit("KitValueRecapSectionSchema", "KitValueRecap", "value-prop"),
  "kit-pay-bar": lazyKit("KitPayBarSectionSchema", "KitPayBar", "closer"),
  "kit-lenta-top": lazyKit("KitLentaTopSectionSchema", "KitLentaTop", "nav"),
  "kit-lenta-hero": lazyKit("KitLentaHeroSectionSchema", "KitLentaHero", "hero"),
  "kit-lenta-gifts": lazyKit("KitLentaGiftsSectionSchema", "KitLentaGifts", "value-prop"),
  "kit-lenta-rows": lazyKit("KitLentaRowsSectionSchema", "KitLentaRows", "value-prop"),
  "kit-lenta-text": lazyKit("KitLentaTextSectionSchema", "KitLentaText", "content"),
  "kit-lenta-schedule": lazyKit("KitLentaScheduleSectionSchema", "KitLentaSchedule", "process"),
  "kit-lenta-about": lazyKit("KitLentaAboutSectionSchema", "KitLentaAbout", "authority"),
  "kit-lenta-faq": lazyKit("KitLentaFaqSectionSchema", "KitLentaFaq", "objection-handling"),
  "kit-benefits": lazyKit("KitBenefitsSectionSchema", "KitBenefits", "value-prop"),
  "kit-stats": lazyKit("KitStatsSectionSchema", "KitStats", "social-proof"),
  "kit-countdown": lazyKit("KitCountdownSectionSchema", "KitCountdown", "closer"),
  "kit-program": lazyKit("KitProgramSectionSchema", "KitProgram", "value-prop"),
  "kit-process": lazyKit("KitProcessSectionSchema", "KitProcess", "process"),
  "kit-case": lazyKit("KitCaseSectionSchema", "KitCase", "social-proof"),
  "kit-links": lazyKit("KitLinksSectionSchema", "KitLinks", "value-prop"),
  "kit-sale-hero": lazyKit("KitSaleHeroSectionSchema", "KitSaleHero", "hero"),
  "kit-sale-tiers": lazyKit("KitSaleTiersSectionSchema", "KitSaleTiers", "pricing"),
  "kit-sale-bonus": lazyKit("KitSaleBonusSectionSchema", "KitSaleBonus", "value-prop"),
  "kit-sale-leadmagnet": lazyKit(
    "KitSaleLeadMagnetSectionSchema",
    "KitSaleLeadMagnet",
    "lead-capture",
  ),
  "kit-hero-simple": lazyKit("KitHeroSimpleSectionSchema", "KitHeroSimple", "hero"),
  "kit-markdown": lazyKit("KitMarkdownSectionSchema", "KitMarkdown", "content"),
  "kit-ticker": lazyKit("KitTickerSectionSchema", "KitTicker", "content"),
  "thank-you-redirect": {
    schema: "ThankYouRedirectSectionSchema",
    component: "ThankYouRedirect",
    role: "utility",
  },
} satisfies Record<string, SectionDef>;

export type SectionType = keyof typeof manifest;

// The exact subset of ComponentKey/SchemaKey actually referenced by the
// manifest above (derived from the narrow `manifest`, not the widened
// `SECTION_MANIFEST` below) — lets section-components.ts's hand-imported
// `COMPONENTS` map be typed to require exactly those keys, no more, no less.
// The conditional (rather than direct `["component"]`/`["schema"]` indexing)
// distributes over the per-entry literal union instead of erroring on the
// entries that lack one side.
type ManifestValue = (typeof manifest)[keyof typeof manifest];
export type UsedComponentKey = ManifestValue extends { component: infer C extends string }
  ? C
  : never;
export type UsedSchemaKey = ManifestValue extends { schema: infer S extends string } ? S : never;

export const SECTION_MANIFEST: Record<SectionType, SectionDef> = manifest;
